import {
  MemCommonEditorImageRenderInfo,
  MemCommonEditorNoteAttachmentHostingStatus,
  resolveImageRender,
} from "@mem-labs/common-editor";
import { NoteAttachmentHostingStatus } from "@/store/note-attachments/types";

/**
 * Maps a synced `NoteAttachmentHostingStatus` string to the common-editor enum
 * the pure resolver expects. Unrecognized values fall through to `Unknown`
 * (defensive parse fallback — the resolver treats it like the legacy branch).
 */
const toResolverHostingStatus = (
  status: NoteAttachmentHostingStatus | null
): MemCommonEditorNoteAttachmentHostingStatus | null => {
  if (status === null) return null;
  switch (status) {
    case "PENDING":
      return MemCommonEditorNoteAttachmentHostingStatus.Pending;
    case "READY":
      return MemCommonEditorNoteAttachmentHostingStatus.Ready;
    case "FAILED":
      return MemCommonEditorNoteAttachmentHostingStatus.Failed;
    case "UNKNOWN":
    default:
      return MemCommonEditorNoteAttachmentHostingStatus.Unknown;
  }
};

export type BuildImageRenderInfoParams = {
  hostingStatus: NoteAttachmentHostingStatus | null;
  servingUrl: string | null;
  /** The doc/external URL stamped on the node, or null. */
  sourceUrl: string | null;
  /** Synchronously-known cached object URL for these bytes, or null. */
  cachedBlobUrl: string | null;
  /** True when this PENDING image is a local upload in flight (vs. external). */
  isLocalUpload: boolean;
  isOnline: boolean;
};

/**
 * Builds the render-resolver verdict carried on the CU info object. The
 * decision itself is delegated to common-editor's pure `resolveImageRender`,
 * which web and iOS share.
 */
export const buildImageRenderInfo = ({
  hostingStatus,
  servingUrl,
  sourceUrl,
  cachedBlobUrl,
  isLocalUpload,
  isOnline,
}: BuildImageRenderInfoParams): MemCommonEditorImageRenderInfo | undefined => {
  return resolveImageRender({
    blobUrl: cachedBlobUrl,
    hostingStatus: toResolverHostingStatus(hostingStatus),
    servingUrl,
    sourceUrl,
    isLocalUpload,
    isOnline,
  });
};
